import {useCallback, useEffect, useState} from 'react';
import {useService, EmployeesApiService, useDebounce} from '@innowise-group/core';
import {Subscription} from 'rxjs';

const DEBOUNCE_DELAY = 500;

interface EmployeeOption {
  value: string;
  title: string;
  firstName: string;
  lastName: string;
  fullNameRu: string;
}

export const useGetEmployees = (employeeName: string) => {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedName = useDebounce(employeeName, DEBOUNCE_DELAY);

  const employeesApi = useService(EmployeesApiService);

  useEffect(() => {
    let isSubscribed = true;
    let subscription: Subscription;

    const fetchEmployees = async () => {
      try {
        setIsLoading(true);
        subscription = employeesApi.getEmployees({name: debouncedName}).subscribe({
          next: ({employees}) => {
            if (isSubscribed) {
              setEmployees(
                employees.map(({id, fullName, firstName, lastName, preferredName}) => {
                  return {
                    value: id,
                    title: fullName,
                    firstName,
                    lastName,
                    fullNameRu: preferredName,
                  };
                }),
              );
            }
          },
          complete: () => {
            if (isSubscribed) {
              setIsLoading(false);
            }
          },
        });
      } catch (err) {
        if (isSubscribed) {
          setEmployees([]);
          setIsLoading(false);
        }
      }
    };

    fetchEmployees();

    return () => {
      isSubscribed = false;
      subscription?.unsubscribe();
    };
  }, [debouncedName, employeesApi]);

  const valueTransform = useCallback(
    async (id: string): Promise<string> => {
      if (!id) {
        return;
      }

      const employeeById = employees.find((employee) => employee.value === id);
      if (employeeById) {
        return employeeById.title;
      }

      return new Promise((resolve, reject) => {
        const subscription = employeesApi.getEmployeeById(id).subscribe({
          next: (employee) => {
            resolve(employee.fullName);
          },
          error: (err) => reject(err.message),
        });

        return () => subscription.unsubscribe();
      });
    },
    [employees, employeesApi],
  );

  return {
    employees,
    isLoading,
    valueTransform,
  };
};
